import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MyRegistration, PlayerStatus } from '@swiss-magic/shared-types';
import { Info, Send, Swords, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AppHeader } from '@/components/AppHeader';
import { getMyRegistrations } from '@/lib/api/player';
import { getMessageThreads } from '@/lib/api/messages';
import { enroll, unenroll } from '@/lib/api/enrollment';
import { unfollowTournament } from '@/lib/api/follow';
import { ApiError } from '@/lib/api/core';
import { usePolledData } from '@/lib/usePolledData';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { DashboardFilterToolbar } from '@/lib/dashboard-filters';
import { usePlayerDashboardFilters } from './player-dashboard/usePlayerDashboardFilters';

// Toolbar appears once the player has accumulated enough items
// (enrollments + follows) to need filtering. Same 5+ threshold the
// director surface uses.
const TOOLBAR_MIN_ITEMS = 5;

const STANDINGS_VISIBLE_STATUSES = ['registration_closed', 'in_progress', 'completed'] as const;

const MY_MATCHES_TOURNAMENT_STATUSES = [
  'registration_open',
  'registration_closed',
  'in_progress',
  'completed',
] as const;
const MY_MATCHES_PLAYER_STATUSES = ['accepted', 'withdrawn', 'eliminated'] as const;

function statusLabel(status: PlayerStatus, t: TFunction): string {
  return t(`dashboard.status.${status}`);
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function PlayerDashboardPage() {
  const { t } = useTranslation('player');
  const navigate = useNavigate();
  // 5-second polling so the dashboard reflects organizer-driven status
  // changes (accept / reject / unblock / etc.) without a manual reload.
  // Same pattern as MyMatches and the public spectator pages: paused on
  // hidden tabs, with AbortController-aware in-flight cancellation.
  const { data: registrations, loading, message, refetch } = usePolledData<MyRegistration[]>(
    () =>
      getMyRegistrations().catch((err: unknown) => {
        throw err instanceof ApiError ? new Error(err.userMessage) : err;
      }),
    [],
  );
  useEffect(() => {
    if (message) showErrorToast(message);
  }, [message]);

  const filters = usePlayerDashboardFilters(registrations ?? []);
  const showToolbar = (registrations?.length ?? 0) >= TOOLBAR_MIN_ITEMS;
  // Below the toolbar threshold there's no way to un-hide the
  // 'past' items the toolbar would otherwise filter out by default,
  // so we render the unfiltered list. (For the director surface
  // this is moot — no chips are hidden by default — but the same
  // pattern would apply there too.)
  const visibleItems = showToolbar ? filters.filteredItems : (registrations ?? []);

  // Soft-confirm dialog for the destructive Unregister action — same
  // posture as the public-page Unregister, just hosted on the dashboard
  // so the user doesn't need to round-trip to the tournament page.
  const [unregisterTarget, setUnregisterTarget] = useState<{
    tournamentId: string;
    tournamentName: string;
  } | null>(null);
  const [submittingAction, setSubmittingAction] = useState(false);

  async function handleUnfollow(tournamentId: string) {
    setSubmittingAction(true);
    try {
      await unfollowTournament(tournamentId);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('dashboard.errors.unfollow_failed'));
    } finally {
      setSubmittingAction(false);
    }
  }

  // Jump straight into the message-center thread with this tournament's
  // director: reuse the existing thread when there is one, otherwise open
  // the composer pre-targeted at the tournament.
  async function handleMessageDirector(tournamentId: string) {
    try {
      const { threads } = await getMessageThreads();
      const existing = threads.find((th) => th.tournamentId === tournamentId);
      navigate(
        existing
          ? `/player/messages/${existing.id}`
          : `/player/messages/new?tournamentId=${encodeURIComponent(tournamentId)}`,
      );
    } catch {
      navigate(
        `/player/messages/new?tournamentId=${encodeURIComponent(tournamentId)}`,
      );
    }
  }

  async function handleRegister(slug: string, tournamentId: string) {
    setSubmittingAction(true);
    try {
      await enroll(tournamentId);
      refetch();
    } catch (err) {
      // Profile-incomplete is the one rich-response error worth handling
      // specially: bounce the player to the profile page with a
      // registerFor intent so the existing onboarding flow takes over.
      if (
        err instanceof ApiError &&
        err.body &&
        Array.isArray((err.body as { missingFields?: unknown }).missingFields)
      ) {
        navigate(`/profile?registerFor=${encodeURIComponent(slug)}`);
        return;
      }
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('dashboard.errors.register_failed'));
    } finally {
      setSubmittingAction(false);
    }
  }

  async function handleConfirmUnregister() {
    if (!unregisterTarget) return;
    setSubmittingAction(true);
    try {
      await unenroll(unregisterTarget.tournamentId);
      showSuccessToast(
        t('dashboard.unregistered_success', {
          tournamentName: unregisterTarget.tournamentName,
        }),
      );
      setUnregisterTarget(null);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('dashboard.errors.unregister_failed'));
    } finally {
      setSubmittingAction(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>{t('dashboard.loading')}</p>
      </div>
    );
  }

  return (
    <div data-testid="player-dashboard-page" className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-6 py-8">
        <h2 className="mb-6 text-2xl font-semibold">{t('dashboard.title')}</h2>

        {(registrations ?? []).length === 0 ? (
          <p
            data-testid="player-dashboard-empty-state"
            className="text-muted-foreground"
          >
            {t('dashboard.empty')}
          </p>
        ) : (
          <>
            {showToolbar && (
              <DashboardFilterToolbar
                filters={filters}
                i18nNamespace="player"
                i18nPrefix="dashboard.toolbar"
              />
            )}
            {showToolbar && visibleItems.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center gap-3 py-12">
                  <p
                    data-testid="dashboard-filter-empty-state"
                    className="text-muted-foreground"
                  >
                    {t('dashboard.toolbar.empty_state')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="dashboard-clear-filters-button"
                    onClick={filters.clearAll}
                  >
                    {t('dashboard.toolbar.clear_filters')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
          <div className="space-y-3">
            {visibleItems.map((r) => (
              <Card
                key={r.tournamentId}
                data-testid={`player-dashboard-card-${r.tournamentName}`}
              >
                <CardContent className="flex flex-col gap-3 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{r.tournamentName}</p>
                      {r.tournamentStartDate && (
                        <p className="text-sm text-muted-foreground">
                          {formatDate(r.tournamentStartDate)}
                        </p>
                      )}
                    </div>
                    <Badge
                      data-testid={`player-dashboard-card-status-${r.tournamentName}`}
                      variant="secondary"
                    >
                      {r.kind === 'follow'
                        ? t('dashboard.status.following')
                        : statusLabel(r.enrollmentStatus!, t)}
                    </Badge>
                  </div>
                  {r.kind === 'enrollment' &&
                    (r.enrollmentStatus === 'rejected' ||
                      r.enrollmentStatus === 'blocked') &&
                    r.rejectionReason && (
                      <p
                        data-testid={`player-dashboard-card-rejection-reason-${r.tournamentName}`}
                        className="text-sm text-muted-foreground"
                      >
                        {t('dashboard.rejection_note', { reason: r.rejectionReason })}
                      </p>
                    )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`player-dashboard-tournament-info-${r.tournamentName}`}
                      onClick={() => navigate(`/t/${r.tournamentSlug}`)}
                    >
                      <Info className="mr-1 h-3 w-3" />
                      {t('dashboard.buttons.tournament_info')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`player-dashboard-message-director-${r.tournamentName}`}
                      onClick={() => handleMessageDirector(r.tournamentId)}
                    >
                      <Send className="mr-1 h-3 w-3" />
                      {t('dashboard.buttons.message_director')}
                    </Button>
                    {(STANDINGS_VISIBLE_STATUSES as readonly string[]).includes(
                      r.tournamentStatus,
                    ) && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`player-dashboard-standings-${r.tournamentName}`}
                        onClick={() => navigate(`/t/${r.tournamentSlug}/standings`)}
                      >
                        <Trophy className="mr-1 h-3 w-3" />
                        {t('dashboard.buttons.standings')}
                      </Button>
                    )}
                    {r.kind === 'enrollment' &&
                      (MY_MATCHES_TOURNAMENT_STATUSES as readonly string[]).includes(
                        r.tournamentStatus,
                      ) &&
                      (MY_MATCHES_PLAYER_STATUSES as readonly string[]).includes(
                        r.enrollmentStatus!,
                      ) && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`player-dashboard-my-matches-${r.tournamentName}`}
                          onClick={() =>
                            navigate(`/player/tournaments/${r.tournamentSlug}/matches`)
                          }
                        >
                          <Swords className="mr-1 h-3 w-3" />
                          {t('dashboard.buttons.my_matches')}
                        </Button>
                      )}
                    {r.kind === 'follow' &&
                      r.tournamentStatus === 'registration_open' && (
                        <Button
                          size="sm"
                          data-testid={`player-dashboard-register-${r.tournamentName}`}
                          disabled={submittingAction}
                          onClick={() =>
                            handleRegister(r.tournamentSlug, r.tournamentId)
                          }
                        >
                          {t('dashboard.buttons.register')}
                        </Button>
                      )}
                    {r.kind === 'follow' && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`player-dashboard-unfollow-${r.tournamentName}`}
                        disabled={submittingAction}
                        onClick={() => handleUnfollow(r.tournamentId)}
                      >
                        {t('dashboard.buttons.unfollow')}
                      </Button>
                    )}
                    {r.kind === 'enrollment' &&
                      r.tournamentStatus === 'registration_open' &&
                      (r.enrollmentStatus === 'none' ||
                        r.enrollmentStatus === 'accepted') && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`player-dashboard-unregister-${r.tournamentName}`}
                          disabled={submittingAction}
                          onClick={() =>
                            setUnregisterTarget({
                              tournamentId: r.tournamentId,
                              tournamentName: r.tournamentName,
                            })
                          }
                        >
                          {t('dashboard.buttons.unregister')}
                        </Button>
                      )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
            )}
          </>
        )}
      </main>

      <Dialog
        open={unregisterTarget !== null}
        onOpenChange={(open) => {
          if (!submittingAction && !open) setUnregisterTarget(null);
        }}
      >
        <DialogContent data-testid="unregister-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.unregister_dialog.title', {
                tournamentName: unregisterTarget?.tournamentName ?? '',
              })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('dashboard.unregister_dialog.body')}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="unregister-cancel-button"
              onClick={() => setUnregisterTarget(null)}
              disabled={submittingAction}
            >
              {t('dashboard.unregister_dialog.cancel')}
            </Button>
            <Button
              variant="destructive"
              data-testid="unregister-confirm-button"
              onClick={handleConfirmUnregister}
              disabled={submittingAction}
            >
              {submittingAction
                ? t('dashboard.unregister_dialog.confirming')
                : t('dashboard.unregister_dialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
